import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, Clock, FileText, ListChecks, IndianRupee, ShieldCheck, Sparkles, UploadCloud, Phone } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { PHONE } from "@/lib/data";
import SEO from "@/components/SEO";
import { getArticleSchema, getFAQPageSchema } from "@/lib/seoSchemas";

const SectionHeading = ({ icon: SIcon, children }) => (
  <h2 className="flex items-center gap-2.5 text-2xl font-semibold tracking-tight text-navy-900 md:text-3xl">
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald2-500/10 text-emerald2-600">
      <SIcon className="h-4 w-4" />
    </span>
    {children}
  </h2>
);

const BulletList = ({ items, tone = "emerald" }) => (
  <ul className="mt-5 space-y-3">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-600">
        <Check className={`mt-1 h-4 w-4 shrink-0 ${tone === "gold" ? "text-gold-600" : "text-emerald2-600"}`} />
        {item}
      </li>
    ))}
  </ul>
);

const normalize = (row) => ({
  slug: row.slug,
  category: row.category,
  title: row.title,
  subtitle: row.subtitle,
  readTime: row.read_time,
  img: row.image,
  intro: row.intro,
  ...row.sections,
});

export default function BlogPost() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setNotFound(false);
    Promise.all([api.getBlog(slug), api.listBlogs()])
      .then(([{ post }, { posts }]) => {
        const normalized = normalize(post);
        setGuide(normalized);
        setRelated(
          (normalized.related || [])
            .map((relSlug) => posts.find((p) => p.slug === relSlug))
            .filter(Boolean)
            .slice(0, 3)
            .map(normalize)
        );
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Posts written in the rich-text format carry a single sanitised HTML body
  // instead of the fixed sections (see AdminContent's format toggle).
  const isRich = guide?.format === "rich";

  if (notFound) return <Navigate to="/blogs" replace />;

  if (loading || !guide) {
    return (
      <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="blog-post-page">
        <ScrollProgress />
        <Navbar />
        <main className="pt-40 pb-20 text-center text-slate-500">Loading...</main>
        <Footer />
        <WhatsAppFab />
      </div>
    );
  }

  const canonicalUrl = `https://www.biz360compliance.com/blogs/${guide.slug}`;
  const articleSchema = getArticleSchema(guide, canonicalUrl);
  const faqSchema = getFAQPageSchema(guide.faqs);
  const schemas = [articleSchema, faqSchema].filter(Boolean);

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="blog-post-page">
      <SEO
        title={`${guide.title} | Biz360 Compliance`}
        description={guide.subtitle || (guide.intro ? guide.intro.slice(0, 150) : "")}
        canonical={`/blogs/${guide.slug}`}
        ogType="article"
        ogImage={guide.img}
        breadcrumbs={[
          { label: "Knowledge Centre", href: "/blogs" },
          { label: guide.title, href: `/blogs/${guide.slug}` },
        ]}
        schema={schemas}
      />
      <ScrollProgress />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-14 md:pt-40 md:pb-16">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-4xl px-5 lg:px-8">
            <Reveal>
              <Link
                to="/blogs"
                data-testid="blog-back-link"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-700 transition-colors hover:text-gold-600"
              >
                <ArrowLeft className="h-4 w-4" /> Back to Knowledge Centre
              </Link>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                  {guide.category}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                  <Clock className="h-3.5 w-3.5" /> {guide.readTime}
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-navy-900 md:text-5xl">{guide.title}</h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-4 max-w-2xl text-base text-slate-600 md:text-lg">{guide.subtitle}</p>
            </Reveal>
          </div>
        </section>

        <div className="relative mx-auto max-w-4xl px-5 py-14 lg:px-8 md:py-20">
          <Reveal>
            <div className="overflow-hidden rounded-3xl">
              <img src={guide.img} alt={guide.title} className="h-64 w-full object-cover md:h-96" loading="lazy" />
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <p className="mt-10 text-[17px] leading-[1.8] text-slate-700">{guide.intro}</p>
          </Reveal>

          {isRich ? (
            <Reveal>
              <div
                className="prose-blog mt-12"
                data-testid="blog-rich-body"
                dangerouslySetInnerHTML={{ __html: guide.body || "" }}
              />
            </Reveal>
          ) : (
            <div className="mt-14 space-y-14">
            <Reveal>
              <SectionHeading icon={ShieldCheck}>{guide.advantages.heading}</SectionHeading>
              <BulletList items={guide.advantages.items} />
            </Reveal>

            <Reveal>
              <SectionHeading icon={ListChecks}>{guide.audience.heading}</SectionHeading>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {guide.audience.items.map((item) => (
                  <span key={item} className="rounded-full border border-navy-900/10 bg-navy-50 px-4 py-2 text-sm font-medium text-navy-800">
                    {item}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <SectionHeading icon={FileText}>{guide.eligibility.heading}</SectionHeading>
              <BulletList items={guide.eligibility.items} tone="gold" />
            </Reveal>

            <Reveal>
              <SectionHeading icon={FileText}>{guide.documents.heading}</SectionHeading>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {guide.documents.groups.map((g) => (
                  <div key={g.title} className="rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-5">
                    <h3 className="text-sm font-bold text-navy-900">{g.title}</h3>
                    <ul className="mt-3 space-y-2">
                      {g.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald2-600" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <SectionHeading icon={ListChecks}>{guide.process.heading}</SectionHeading>
              <ol className="mt-6 space-y-5">
                {guide.process.steps.map((step, i) => (
                  <motion.li
                    key={step.title}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: false, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: i * 0.04 }}
                    className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_4px_16px_rgba(0,0,0,0.03)]"
                    data-testid={`guide-step-${i}`}
                  >
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-navy-900 text-sm font-bold text-gold-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h4 className="font-semibold text-navy-900">{step.title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{step.desc}</p>
                    </div>
                  </motion.li>
                ))}
              </ol>
            </Reveal>

            <Reveal>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="rounded-2xl border border-emerald2-300/40 bg-emerald2-500/[0.06] p-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-emerald2-700">{guide.timeline.heading}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">{guide.timeline.body}</p>
                </div>
                <div className="rounded-2xl border border-gold-300/50 bg-gold-500/[0.06] p-6">
                  <h3 className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-gold-700">
                    <IndianRupee className="h-4 w-4" /> Fees Depend On
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">{guide.fees.note}</p>
                </div>
              </div>
            </Reveal>

            <Reveal>
              <SectionHeading icon={ShieldCheck}>{guide.compliance.heading}</SectionHeading>
              <BulletList items={guide.compliance.items} />
            </Reveal>

            <Reveal>
              <div className="rounded-3xl bg-navy-900 p-8 md:p-10">
                <SectionHeading icon={ShieldCheck}>
                  <span className="text-white">{guide.whyUs.heading}</span>
                </SectionHeading>
                <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {guide.whyUs.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-white/75">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal>
              <SectionHeading icon={FileText}>Frequently Asked Questions</SectionHeading>
              <Accordion type="single" collapsible className="mt-4 divide-y divide-navy-900/10" data-testid="guide-faq-accordion">
                {guide.faqs.map((item, i) => (
                  <AccordionItem key={item.q} value={`gfaq-${i}`} className="border-b border-navy-900/10 py-1.5">
                    <AccordionTrigger className="text-left text-base font-semibold text-navy-900 hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-slate-600">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
            </div>
          )}

          {/* CTA */}
          <Reveal>
            <div className="mt-16 flex flex-col items-start gap-6 rounded-3xl border border-navy-900/10 bg-[rgb(238,242,250)] p-8 md:flex-row md:items-center md:justify-between md:p-10">
              <div>
                <h3 className="text-xl font-semibold text-navy-900 md:text-2xl">Ready to get started with {guide.title.split(" Registration")[0]}?</h3>
                <p className="mt-2 text-sm text-slate-600 md:text-base">Talk to our team for a free, no-obligation consultation.</p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-3.5">
                {/* Button 1: Book Free Consultation */}
                <Link
                  to="/contact"
                  data-testid="guide-cta-contact"
                  className="group relative inline-flex items-center gap-2.5 whitespace-nowrap rounded-full bg-[#f2a40b] px-4 py-2.5 text-xs sm:px-5 sm:py-3 sm:text-sm font-bold text-[#08254a] shadow-[0_8px_22px_rgba(242,164,11,0.35)] ring-1 ring-amber-400/40 transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_12px_28px_rgba(242,164,11,0.5)] active:scale-[0.98]"
                >
                  <span className="grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-black/10 transition-transform duration-300 group-hover:rotate-12">
                    <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#08254a]" />
                  </span>
                  <span className="font-bold tracking-tight">Book Free Consultation</span>
                  <span className="grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-black/10 transition-transform duration-300 group-hover:translate-x-0.5">
                    <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#08254a]" />
                  </span>
                </Link>

                {/* Button 2: Upload Documents */}
                <Link
                  to="/upload"
                  data-testid="guide-cta-upload"
                  className="group relative inline-flex items-center gap-2.5 whitespace-nowrap rounded-full bg-[#1970f6] px-4 py-2.5 text-xs sm:px-5 sm:py-3 sm:text-sm font-bold text-white shadow-[0_8px_22px_rgba(25,112,246,0.35)] ring-1 ring-blue-400/40 transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_12px_28px_rgba(25,112,246,0.5)] active:scale-[0.98]"
                >
                  <span className="grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-white/20 transition-transform duration-300 group-hover:-translate-y-0.5">
                    <UploadCloud className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                  </span>
                  <span className="font-bold tracking-tight">Upload Documents</span>
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-white shadow-sm" />
                </Link>

                {/* Button 3: Telephone CTA */}
                <a
                  href={`tel:${PHONE.replace(/\s+/g, "")}`}
                  data-testid="guide-cta-phone"
                  className="group inline-flex items-center gap-2.5 whitespace-nowrap rounded-full border border-slate-200/90 bg-white px-4 py-2.5 text-xs sm:px-5 sm:py-3 sm:text-sm font-bold text-[#0b2a54] shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-all duration-300 hover:border-emerald-500/40 hover:scale-[1.02] hover:shadow-[0_8px_22px_rgba(22,122,61,0.15)] active:scale-[0.98]"
                >
                  <span className="grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-[#167a3d] text-white shadow-sm transition-transform duration-300 group-hover:rotate-12">
                    <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white fill-white/20" />
                  </span>
                  <span className="font-bold tracking-tight text-[#0b2a54]">{PHONE}</span>
                  <span className="relative flex h-2.5 w-2.5 shrink-0 items-center justify-center">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#21be89] opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#21be89]" />
                  </span>
                </a>
              </div>
            </div>
          </Reveal>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-16">
              <Reveal>
                <h3 className="text-xl font-semibold text-navy-900 md:text-2xl">Related Guides</h3>
              </Reveal>
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
                {related.map((r, i) => (
                  <Reveal key={r.slug} delay={i * 0.05}>
                    <Link
                      to={`/blogs/${r.slug}`}
                      data-testid={`related-guide-${r.slug}`}
                      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_16px_36px_rgba(15,30,60,0.1)]"
                    >
                      <div className="h-32 overflow-hidden">
                        <img src={r.img} alt={r.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <h4 className="text-sm font-semibold leading-snug text-navy-900 group-hover:text-gold-600">{r.title}</h4>
                        <span className="mt-auto flex items-center gap-1 pt-4 text-xs font-semibold text-emerald2-600">
                          Read guide <ArrowUpRight className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
